import React from "react";
import { Link } from "react-router-dom";
import { Briefcase, FileText, Users, IndianRupee, ArrowRight, PlusCircle } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState, LoadingState } from "../../components/shared/EmptyState";
import { companyNav } from "../../components/shared/navConfig";
import { useAuth } from "../../context/AppContext";
import { useApi, inr } from "../../lib/useApi";

export default function CompanyDashboard() {
  const { user } = useAuth();
  const tasks = useApi("/tasks/mine");
  const rooms = useApi("/workrooms/mine");
  const pay = useApi("/payments/records");
  const list = tasks.data || [];
  const roomList = rooms.data || [];

  return (
    <DashboardShell nav={companyNav} title={`Welcome, ${user?.name || "there"}`} subtitle="Your tasks and allocated students">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard testId="company-stat-tasks" label="Tasks posted" value={tasks.loading ? "—" : list.filter(t => t.status !== "Draft").length} icon={FileText} accent/>
        <StatCard testId="company-stat-live" label="Live" value={tasks.loading ? "—" : list.filter(t => t.status === "Approved").length} icon={Briefcase}/>
        <StatCard testId="company-stat-allocated" label="Allocated" value={rooms.loading ? "—" : roomList.length} icon={Users}/>
        <StatCard testId="company-stat-payable" label="Payable" value={pay.data ? inr(pay.data.totals?.Payable) : "—"} icon={IndianRupee}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 card-surface p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div><div className="eyebrow">My tasks</div><h3 className="font-display text-lg mt-1" style={{ color: "var(--text)" }}>Recent submissions</h3></div>
            <Link to="/company/tasks" className="text-xs text-blue-500 inline-flex items-center gap-1" data-testid="company-view-all-tasks">View all <ArrowRight size={12}/></Link>
          </div>
          {tasks.loading ? <LoadingState/> : !list.length ? (
            <EmptyState icon={FileText} title="You haven't posted a task yet" desc="Post your first task — admin reviews it before students can apply." cta="Post a task" to="/company/post" testId="company-tasks-empty"/>
          ) : (
            <table className="table-stack w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest text-left" style={{ color: "var(--text-dim)" }}><th className="pb-3 font-normal">Task</th><th className="pb-3 font-normal">Location</th><th className="pb-3 font-normal">Budget</th><th className="pb-3 font-normal">Deadline</th><th className="pb-3 font-normal text-right">Status</th></tr></thead>
              <tbody>{list.slice(0, 6).map(t => (
                <tr key={t.task_id} data-testid={`company-task-row-${t.task_id}`} className="border-t" style={{ borderColor: "var(--border)" }}>
                  <td className="py-3"><div style={{ color: "var(--text)" }}>{t.title}</div><div className="text-[11px] font-mono" style={{ color: "var(--text-dim)" }}>{t.task_id}</div></td>
                  <td data-label="Location" className="text-xs" style={{ color: "var(--text-muted)" }}><span>{t.work_mode === "Remote" ? "Remote" : t.city || "—"}</span></td>
                  <td data-label="Budget" className="text-xs font-mono" style={{ color: "var(--text)" }}><span>{inr(t.budget)}</span></td>
                  <td data-label="Deadline" className="text-xs" style={{ color: "var(--text-muted)" }}><span>{t.deadline || "—"}</span></td>
                  <td data-label="Status" className="text-right"><StatusBadge status={t.status}/></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>

        <div className="card-surface p-4 sm:p-5">
          <div className="eyebrow mb-3">Allocated students</div>
          {rooms.loading ? <LoadingState/> : !roomList.length ? (
            <EmptyState icon={Users} title="No students allocated yet" desc="Once admin allocates a student to your task, their workroom appears here." testId="company-alloc-empty"/>
          ) : roomList.slice(0, 5).map(r => (
            <Link key={r.workroom_id} to={`/workroom?id=${r.workroom_id}`} data-testid={`company-room-${r.workroom_id}`} className="block p-3 mb-2 rounded-md border hover:bg-white/5 transition-colors duration-200" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-start justify-between gap-2"><span className="text-sm truncate" style={{ color: "var(--text)" }}>{r.task?.title || r.task_id}</span><StatusBadge status={r.status}/></div>
            </Link>
          ))}
          <Link to="/company/post" data-testid="company-post-cta" className="btn-primary w-full mt-3 py-2.5 rounded-full text-xs inline-flex items-center justify-center gap-1.5"><PlusCircle size={13}/> Post a task</Link>
        </div>
      </div>
    </DashboardShell>
  );
}
